import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Link } from 'react-router-dom';
import { attorneysService } from '../../../services/attorneysService';
import { motion } from 'framer-motion';

const AttorneySpotlight = () => {
	const [attorneys, setAttorneys] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		loadAttorneys();
	}, []);

	const loadAttorneys = async () => {
		try {
			setLoading(true);
			const { data, error } = await attorneysService.getFeaturedAttorneys();
			if (error) throw error;
			setAttorneys(data || []);
		} catch (error) {
			console.error('Error loading attorneys:', error);
			setError('Failed to load attorneys');
		} finally {
			setLoading(false);
		}
	};

	return (
		<section className='py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden'>
			{/* Background Pattern */}
			<div className='absolute inset-0 opacity-5'>
				<div
					className='absolute inset-0'
					style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23800020' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
					}}
				/>
			</div>

			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative'>
				{/* Header */}
				<div className='text-center mb-16'>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
						className='inline-flex items-center space-x-2 bg-[#800020]/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6'
					>
						<Icon name='Users' size={20} className='text-[#800020]' />
						<span className='text-[#800020] font-source font-medium'>Meet Our Legal Team</span>
					</motion.div>

					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, delay: 0.1 }}
						className='font-playfair font-bold text-4xl lg:text-5xl text-gray-900 mb-6'
					>
						Meet Our Distinguished
						<span className='block text-[#800020]'>Attorneys</span>
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className='text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed'
					>
						Our team of experienced legal professionals brings decades of combined expertise across
						multiple practice areas, ensuring exceptional representation for every client.
					</motion.p>
				</div>

				{/* Attorneys Grid */}
				{loading ? (
					<div className='flex items-center justify-center py-16'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading attorneys...</span>
					</div>
				) : error ? (
					<div className='text-center py-16'>
						<Icon name='AlertCircle' className='h-12 w-12 mx-auto mb-4 text-red-500' />
						<p className='text-red-600 mb-4'>{error}</p>
						<Button
							onClick={loadAttorneys}
							className='bg-[#800020] hover:bg-[#800020]/90 text-white px-6 py-2 rounded-lg'
						>
							Try Again
						</Button>
					</div>
				) : attorneys.length === 0 ? (
					<div className='text-center py-16'>
						<Icon name='Users' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
						<p className='text-gray-500'>No attorneys found</p>
					</div>
				) : (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16'>
						{attorneys.slice(0, 6).map((attorney, index) => (
							<motion.div
								key={attorney.id}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.6, delay: index * 0.1 }}
								className='group text-center'
							>
								<div className='bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden'>
									{/* Portrait Image */}
									<div className='relative aspect-[3/4] overflow-hidden'>
										<Image
											src={attorney.image || '/assets/images/no_image.png'}
											alt={attorney.name}
											className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300'
										/>

										{/* Partner Badge */}
										{attorney.isPartner && (
											<div className='absolute top-4 right-4'>
												<div className='bg-[#800020] text-white px-3 py-1 rounded-full text-xs font-medium'>
													Partner
												</div>
											</div>
										)}
									</div>

									{/* Attorney Info */}
									<div className='p-6'>
										<h3 className='font-playfair font-bold text-xl text-gray-900 mb-2 uppercase tracking-wide'>
											{attorney.name}
										</h3>
										<p className='text-[#800020] font-medium text-lg'>{attorney.title}</p>
									</div>
								</div>
							</motion.div>
						))}
					</div>
				)}

				{/* Why Choose FEPA Law Partners */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className='bg-gradient-to-r from-[#800020] to-[#800020]/90 rounded-2xl p-8 lg:p-12 text-center text-white relative overflow-hidden'
				>
					{/* Background Pattern */}
					<div className='absolute inset-0 opacity-10'>
						<div
							className='absolute inset-0'
							style={{
								backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
							}}
						/>
					</div>

					<div className='relative z-10'>
						<h3 className='font-playfair font-bold text-3xl lg:text-4xl mb-6'>
							Why Choose FEPA Law Partners?
						</h3>
						<p className='text-lg text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed'>
							Our attorneys combine deep legal expertise with a client-first approach, delivering
							results that exceed expectations and build lasting relationships.
						</p>
						<div className='flex flex-col sm:flex-row gap-4 justify-center'>
							<Link to='/contact'>
								<Button
									variant='outline'
									size='lg'
									className='border-2 border-white text-white hover:bg-white hover:text-[#800020] px-8 py-3 rounded-lg font-source font-semibold transition-all duration-300 flex items-center justify-center space-x-2'
								>
									<Icon name='Calendar' size={20} />
									<span>Schedule a Consultation</span>
								</Button>
							</Link>
							<Button
								variant='outline'
								size='lg'
								className='border-2 border-[#DB954E] text-[#DB954E] hover:bg-[#DB954E] hover:text-white px-8 py-3 rounded-lg font-source font-semibold transition-all duration-300 flex items-center justify-center space-x-2'
							>
								<Icon name='Phone' size={20} />
								<span>Call (+234) 909-552-9557</span>
							</Button>
						</div>
					</div>
				</motion.div>
			</div>
		</section>
	);
};

export default AttorneySpotlight;
